import { VALORE_STREAM_URL, parseValoreData, type ValoreQuote } from '../../supabase/functions/_shared/valoreQuotes';

export type ValorePriceData = ValoreQuote;

export type PriceUpdateCallback = (prices: Map<string, ValorePriceData>) => void;

const WEBSOCKET_URL = VALORE_STREAM_URL;
const RECONNECT_BASE_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const PING_INTERVAL = 20000;

class ValoreWebSocketService {
  private ws: WebSocket | null = null;
  private prices: Map<string, ValorePriceData> = new Map();
  private subscribers: Set<PriceUpdateCallback> = new Set();
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private isConnecting = false;
  private connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private statusListeners: Set<(status: string) => void> = new Set();
  private subscribedSymbols: Set<string> = new Set();

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.setStatus('connecting');

    try {
      this.ws = new WebSocket(WEBSOCKET_URL);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.setStatus('connected');
        this.startPing();
        this.startPolling();
        this.subscribeToAllSymbols();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.stopPing();
        this.stopPolling();
        this.setStatus('disconnected');
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('[Valore WS] WebSocket error:', error);
        this.isConnecting = false;
        this.stopPing();
        this.stopPolling();
        this.setStatus('disconnected');
        if (this.ws) {
          this.ws.close();
        }
      };
    } catch {
      this.isConnecting = false;
      this.setStatus('disconnected');
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPing();
    this.stopPolling();

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = false;
    this.setStatus('disconnected');
  }

  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          this.ws?.close();
        }
      }
    }, PING_INTERVAL);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private subscribeToAllSymbols(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const subscribeMessage = {
          type: 'subscribe',
          symbols: ['all']
        };
        this.ws.send(JSON.stringify(subscribeMessage));
      } catch (error) {
        console.error('[Valore WS] Error sending subscription:', error);
      }
    }
  }

  private startPolling(): void {
    this.stopPolling();

    this.pollingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          const requestMessage = {
            type: 'request_snapshot'
          };
          this.ws.send(JSON.stringify(requestMessage));
        } catch (error) {
          console.error('[Valore WS] Error requesting snapshot:', error);
        }
      }
    }, 1000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      if (message.type === 'pong') {
        return;
      }

      if (message.type === 'snapshot' && message.data) {
        this.processSnapshot(message.data);
      } else if (message.type === 'price_update' && message.data) {
        this.processPriceUpdate(message.data);
      }
    } catch (error) {
      console.error('[Valore WS] Error parsing message:', error);
    }
  }

  private processSnapshot(data: unknown): void {
    this.processPriceUpdate(data);
  }

  private processPriceUpdate(data: unknown): void {
    let hasUpdates = false;
    for (const quote of parseValoreData(data)) {
      const previous = this.prices.get(quote.symbol);
      if (previous && previous.timestamp > quote.timestamp) continue;
      this.prices.set(quote.symbol, quote);
      hasUpdates = true;
    }
    if (hasUpdates) this.notifySubscribers();
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, Math.min(this.reconnectAttempts - 1, 10)),
      MAX_RECONNECT_DELAY
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private setStatus(status: 'disconnected' | 'connecting' | 'connected'): void {
    this.connectionStatus = status;
    this.statusListeners.forEach(listener => listener(status));
  }

  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(new Map(this.prices)));
  }

  subscribe(callback: PriceUpdateCallback): () => void {
    this.subscribers.add(callback);

    if (this.prices.size > 0) {
      callback(this.prices);
    }

    return () => {
      this.subscribers.delete(callback);
    };
  }

  onStatusChange(callback: (status: string) => void): () => void {
    this.statusListeners.add(callback);
    callback(this.connectionStatus);

    return () => {
      this.statusListeners.delete(callback);
    };
  }

  getPrice(symbol: string): ValorePriceData | null {
    return this.prices.get(symbol) || null;
  }

  getMidPrice(symbol: string): number {
    const priceData = this.prices.get(symbol);
    return priceData?.mid || 0;
  }

  getBidPrice(symbol: string): number {
    const priceData = this.prices.get(symbol);
    return priceData?.bid || 0;
  }

  getAskPrice(symbol: string): number {
    const priceData = this.prices.get(symbol);
    return priceData?.ask || 0;
  }

  getAllPrices(): Map<string, ValorePriceData> {
    return new Map(this.prices);
  }

  getStatus(): string {
    return this.connectionStatus;
  }

  isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }
}

export const valoreWebSocket = new ValoreWebSocketService();
